// Per-chart-type fixtures for the dev-only render-route tester (see ./index.ts).
// Every DEFAULT_* constant is a verified minimal-valid `data` payload; FIXTURES pairs
// it with default `params` so `testChartExport({ chartType })` works bare. Real staging
// captures live in ./fixtures/*.json; the rest are synthesized (shapes reverse-
// engineered from the column builders — see per-constant comments).
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/architecture-final.md §8b.1

import vkAnswer from '@aytm/app/stats/page/render-shell/test-utils/fixtures/vk.json'
import vkInfographicAnswer from '@aytm/app/stats/page/render-shell/test-utils/fixtures/vk-infographic.json'
import hmAnswer from '@aytm/app/stats/page/render-shell/test-utils/fixtures/hm.json'
import emdAnswer from '@aytm/app/stats/page/render-shell/test-utils/fixtures/emd.json'
import bpmAnswer from '@aytm/app/stats/page/render-shell/test-utils/fixtures/bpm.json'

// Minimal valid info_view/bars payload (respondents mode, no batches). Shape
// derived from the column builder: answerVariants[{position,text}] + answer keyed
// by a{position} + a colorTheme palette.
export const DEFAULT_BARS_DATA = {
  questionKey: 'q1',
  qType: 'cb',
  answerType: 'amount',
  answerVariants: [
    { position: 1, text: 'Option A' },
    { position: 2, text: 'Option B' },
    { position: 3, text: 'Option C' },
    { position: 4, text: 'Option D' }
  ],
  answer: {
    a1: { amount: 40, percents: 40, base: 100 },
    a2: { amount: 35, percents: 35, base: 100 },
    a3: { amount: 25, percents: 25, base: 100 },
    a4: { amount: 18, percents: 18, base: 100 }
  },
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  format: {},
  features: {}
}

// Minimal Word Cloud payload. Each word carries both `times` and `f` so the
// font-size domain resolves regardless of the mergeWords config (mergeField
// = mergeWords ? 'times' : 'f'). config: {} → DEFAULT_CONFIG.
export const DEFAULT_WC_DATA: any = {
  model: { useRawData: false },
  words: [
    { text: 'market', times: 100, f: 100 },
    { text: 'research', times: 80, f: 80 },
    { text: 'survey', times: 60, f: 60 },
    { text: 'insight', times: 45, f: 45 },
    { text: 'data', times: 30, f: 30 },
    { text: 'analysis', times: 20, f: 20 },
    { text: 'audience', times: 12, f: 12 }
  ],
  config: {},
  scope: []
}

// Van Keulen / Westendorp. vk.json is a real `answer` captured from staging
// (product:[1] → Westendorp). Wrapped into Block props; palette must be a
// non-empty array (drawWestenDorpChart indexes into it). components omitted →
// isComponentOn() defaults true → chart renders.
export const DEFAULT_VK_DATA: any = {
  id: 'vk1',
  sectionKey: 'vk1',
  type: 'vk',
  answer: vkAnswer,
  config: { tam: 1000, unit: '$', price: 1 },
  palette: ['#4572A7', '#AA4643', '#89A54E', '#80699B', '#3D96AE', '#DB843D', '#92A8CD', '#A47D7C'],
  text: '',
  fqPeriod: 0,
  reportParams: {}
}

// Infographic mode needs a fuller answer (product:[6], full pricing study) so all
// 4 sub-charts have valid point data. The Westendorp-only vk.json (product:[1])
// lacks purchase-prob/freq/profit data → drawChart produces NaN coords. fqPeriod 4
// is a valid non-zero period from this answer's available_periods.
export const DEFAULT_VK_INFOGRAPHIC_DATA: any = {
  ...DEFAULT_VK_DATA,
  answer: vkInfographicAnswer,
  fqPeriod: 4
}

// Conjoint (cjc/cjd). Synthetic v1-config answer: featureLengths drives batch count,
// adjusted_coefficients (flat, one per batch×option) feeds the logistic position math,
// importance_aggregate backs vasc/vdesc sorts. batches must match featureLengths.
// No currentPackage on purpose — toProps computes the median fallback (mainPackages[2],
// here '2.2.3'), exercising the real default path a driver hits when cjc{key} is empty.
export const DEFAULT_CJ_DATA: any = {
  answer: {
    config: { v: 1 },
    featureLengths: [3, 3, 3],
    adjusted_coefficients: [0.8, 0.2, -0.5, 0.4, -0.1, -0.6, -0.2, 0.6, 0.1],
    importance_aggregate: [40, 35, 25]
  },
  batches: [
    {
      position: 1,
      text: 'Brand',
      answerVariants: [
        { position: 1, text: 'Acme' },
        { position: 2, text: 'Globex' },
        { position: 3, text: 'Initech' }
      ]
    },
    {
      position: 2,
      text: 'Price',
      answerVariants: [
        { position: 1, text: '$9.99' },
        { position: 2, text: '$14.99' },
        { position: 3, text: '$19.99' }
      ]
    },
    {
      position: 3,
      text: 'Size',
      answerVariants: [
        { position: 1, text: 'Small' },
        { position: 2, text: 'Medium' },
        { position: 3, text: 'Large' }
      ]
    }
  ],
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B']
}

// Heatmap composite. hm.json is a real staging `answer` (heatmap.levels/contours/
// clusters/points). The image mirrors the BE question schema (asset_json_image): full
// object with original.{url,path,width,height} — NEVER base64. ActionButtons converts
// the URL to base64 on the render route via loadImageAsBase64 (same as the live page),
// so this fixture needs network access to the asset service for the bg to appear.
// clusters come straight from the answer, so params:{clustersMode:'clusters-on'} draws
// the cluster overlay too. viewMode 'heatmap' is the default; try 'spotlight'/'overlay'/
// 'original' (all backed by the same answer).
export const HM_IMAGE: any = {
  id: 'im_c92d3fb64722cddb5787a96223a68e1b',
  uid: 'stage-gcs-images.aytm.com/2QlqrwIFgjw1TOHOXqsMVjhv.jpeg',
  expand: false,
  original: {
    id: 690277,
    url: 'https://stage-gcs-images.aytm.com/2QlqrwIFgjw1TOHOXqsMVjhv.jpeg',
    path: 'stage-gcs-images.aytm.com/2QlqrwIFgjw1TOHOXqsMVjhv.jpeg',
    title: null,
    width: 746,
    height: 500
  },
  scrolling: false
}

export const HM_BATCHES = [
  { text: 'Most likely', color: '#82BD11', fixed: false, position: 1 },
  { text: 'Least likely, why?', color: '#ce1f1f', fixed: false, comments: true, position: 2 }
]

export const DEFAULT_HM_DATA: any = {
  id: 'hm1',
  questionKey: 'q20',
  image: HM_IMAGE,
  answer: hmAnswer,
  clusters: (hmAnswer as any).heatmap.clusters,
  batches: HM_BATCHES,
  config: {}
}

// Matrix family (mx representative; sr/se/ps/mp/ro/aro share MatrixChart). viewMode
// 'stacked' → stacked_percents builder, which reads answer.total + answer.b{pos}.a{av}
// = {amount, percents}, batches sharing the same answerVariants set (matrix shape).
export const DEFAULT_MX_DATA: any = {
  questionKey: 'q1',
  qType: 'mx',
  answer: {
    total: 100,
    b1: { a1: { amount: 50, percents: 50 }, a2: { amount: 30, percents: 30 }, a3: { amount: 20, percents: 20 } },
    b2: { a1: { amount: 40, percents: 40 }, a2: { amount: 35, percents: 35 }, a3: { amount: 25, percents: 25 } },
    b3: { a1: { amount: 60, percents: 60 }, a2: { amount: 25, percents: 25 }, a3: { amount: 15, percents: 15 } }
  },
  batches: [
    {
      position: 1,
      text: 'Speed',
      answerVariants: [
        { position: 1, text: 'Agree' },
        { position: 2, text: 'Neutral' },
        { position: 3, text: 'Disagree' }
      ]
    },
    {
      position: 2,
      text: 'Price',
      answerVariants: [
        { position: 1, text: 'Agree' },
        { position: 2, text: 'Neutral' },
        { position: 3, text: 'Disagree' }
      ]
    },
    {
      position: 3,
      text: 'Design',
      answerVariants: [
        { position: 1, text: 'Agree' },
        { position: 2, text: 'Neutral' },
        { position: 3, text: 'Disagree' }
      ]
    }
  ],
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  format: {}
}

// MaxDiff (md). Raw answer keyed b{n}:{varyingProbability,probability,raw}; batches are
// the items. viewMode 'probability' → hcAnswers.a{n}.amount = probability, yAxis [0,100].
export const DEFAULT_MD_DATA: any = {
  questionKey: 'q1',
  qType: 'md',
  answer: {
    total: 200,
    b1: { probability: 42, varyingProbability: 48, raw: 0.9 },
    b2: { probability: 31, varyingProbability: 35, raw: 0.4 },
    b3: { probability: 18, varyingProbability: 12, raw: -0.2 },
    b4: { probability: 9, varyingProbability: 5, raw: -0.8 }
  },
  batches: [
    { position: 1, text: 'Fast delivery' },
    { position: 2, text: 'Low price' },
    { position: 3, text: 'Premium quality' },
    { position: 4, text: 'Eco-friendly' }
  ],
  itemsPerSet: 4,
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  format: {}
}

// Rapid Association (sft). viewMode 'bar' → column chart; answer keyed a{position} like
// bars (amount+percents+total). answerVariants are the entities.
export const DEFAULT_SFT_DATA: any = {
  questionKey: 'q1',
  qType: 'sft',
  answerVariants: [
    { position: 1, text: 'Brand X' },
    { position: 2, text: 'Brand Y' },
    { position: 3, text: 'Brand Z' }
  ],
  answer: {
    total: 100,
    a1: { amount: 60, percents: 60 },
    a2: { amount: 45, percents: 45 },
    a3: { amount: 30, percents: 30 }
  },
  btnsType: 'like',
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  format: {}
}

// Correlation Finder (cf). groupType 'grouped' → dl_columns builder: categories = batches,
// series = batches[0].answerVariants; answer keyed b{pos}.a{av} = {amount, percents} + total
// (same batched shape as matrix). axisType 'respondents' → reads amount.
export const DEFAULT_CF_DATA: any = {
  questionKey: 'q1',
  qType: 'cf',
  answerType: 'amount',
  answer: {
    total: 100,
    b1: { total: 100, a1: { amount: 60, percents: 60 }, a2: { amount: 40, percents: 40 } },
    b2: { total: 100, a1: { amount: 45, percents: 45 }, a2: { amount: 55, percents: 55 } }
  },
  batches: [
    {
      position: 1,
      text: 'Product A',
      answerVariants: [
        { position: 1, text: 'Yes' },
        { position: 2, text: 'No' }
      ]
    },
    {
      position: 2,
      text: 'Product B',
      answerVariants: [
        { position: 1, text: 'Yes' },
        { position: 2, text: 'No' }
      ]
    }
  ],
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  format: {}
}

// Polarity Scale (psc). answer keyed b{position}:{values,weights}; values are
// INTEGERS on the raw slider scale ±floor(degree/2) — real server data is ±100 with
// degree 201, so power = floor(201/2)/100 = 1.0 and percents = |median/mean| ≈ 40–60.
// (A ±1.0 float scale renders 1px sliver bars — that was the first-iteration bug.)
// weights:null → unweighted counts. batch.text is the two pole labels (array form,
// as ChartRow consumes it; the JSON-string DB form also works via parseText in toProps).
export const DEFAULT_PSC_DATA: any = {
  id: 'psc1',
  degree: 201,
  answer: {
    b1: {
      // right-skewed: strong "Expensive" pull
      values: [-89, -57, -45, -36, -31, -22, 0, 11, 18, 28, 33, 40, 45, 46, 51, 54, 60, 62, 65, 74, 84, 86, 100, 100],
      weights: null
    },
    b2: {
      // left-skewed: strong "Boring" pull
      values: [
        -100, -100, -92, -69, -68, -65, -53, -49, -48, -45, -41, -30, -25, -14, 0, 17, 18, 29, 35, 38, 38, 50, 64, 67
      ],
      weights: null
    },
    b3: {
      // near-balanced with a small neutral band
      values: [-100, -80, -63, -47, -41, -31, -20, -15, -3, 0, 0, 3, 12, 15, 24, 33, 36, 43, 51, 54, 62, 72, 75, 100],
      weights: null
    }
  },
  batches: [
    { position: 1, text: ['Cheap', 'Expensive'] },
    { position: 2, text: ['Boring', 'Exciting'] },
    { position: 3, text: ['Outdated', 'Innovative'] }
  ],
  palette: ['#4572A7', '#AA4643', '#89A54E', '#80699B']
}

// Info-View histogram (histogram). answer.hist[period] = bin {x,y} arrays,
// answer.curve[period] = smoothed overlay (same-length x/y). bars lists the
// available bin counts (string keys into hist/curve); period must be one of them.
export const DEFAULT_HISTOGRAM_DATA: any = {
  id: 'iv1',
  answer: {
    hist: {
      '10': {
        x: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
        y: [2, 5, 9, 14, 18, 16, 12, 8, 4, 2]
      }
    },
    curve: {
      '10': {
        x: [0.5, 1.5, 2.5, 3.5, 4.5, 5.5, 6.5, 7.5, 8.5, 9.5, 10.5],
        y: [1, 3, 7, 12, 17, 18, 15, 10, 6, 3, 1]
      }
    }
  },
  bars: ['10'],
  ticks: { x: null, y: null },
  axis: { x: 'Price, $', y: 'Respondents' },
  palette: ['#4572A7', '#AA4643', '#89A54E', '#80699B']
}

// Shelf Test (sh). Reuses hm.json's real heatmap capture — the sh renderer consumes
// the identical {levels, contours[.contour], *_by_polygons, points} shape under
// answer.shelftestHeatmap — plus the same staging image as preview.original (the
// export step fetches https://{path} directly, like the live Download button).
// shelfType plain (margins 0); store must not be 'ecommerce' (no composite) and
// 'publix' adds +18 marginY.
export const DEFAULT_SH_DATA: any = {
  id: 'sh1',
  questionKey: 'q30',
  shelf_preview_data: { id: 'shelfprev1', store: 'walmart' },
  shelfType: 'shelf',
  answer: { shelftestHeatmap: (hmAnswer as any).heatmap },
  preview: { original: HM_IMAGE.original }
}

// Emotional Dial (emd). emd.json is a real staging `answer` — everything lives under
// answer.emd.b1: meanLine/meanLineSmooth/segmentedLine as [x,y] pairs (x seconds,
// y 0..100 with 50 = neutral), pointDensity {timeStr: density[49]} (its keys ARE the
// timeVals, 300 points over ~11s; values drive the timeslice ticks), heatmap
// levels/contours (contour x in seconds, y 0..100), windmap 300×101 grid with flat
// dx/dy flow vectors (zero cells are falsy → dead, no arrows there). videoDuration is
// NOT in the answer (live page reads the <video> element) — the last changePoint /
// pointDensity key is 11. batches[0] carries the pole labels (text[0] bottom /
// text[1] top) + colors ([line, gradient-top]).
export const DEFAULT_EMD_DATA: any = {
  id: 'emd1',
  questionKey: 'q40',
  qType: 'emd',
  answer: emdAnswer,
  batches: [{ position: 1, text: ['Boring', 'Engaging'], colors: ['#444444', '#44A9A5'] }],
  videoDuration: 11,
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  features: {}
}

// Open-ended (oe). answer.cloudWords is the BE-processed corpus: words are ARRAYS
// [text, frequency, stopLevel] (stopLevel 0 = never filtered; 1..3 = filtered when
// config.stop1..3 = 1), plus optional sentences/spell/noun/index maps (omitted here —
// useWordProcessor defaults them). wcConfig (report-param wc{id}) is empty → preset
// defaults.
export const DEFAULT_OE_DATA: any = {
  id: 'oe1',
  questionKey: 'q50',
  qType: 'oe',
  answer: {
    cloudWords: {
      words: [
        ['market', 100, 0],
        ['research', 80, 0],
        ['survey', 60, 0],
        ['insight', 45, 0],
        ['data', 30, 0],
        ['analysis', 20, 0],
        ['audience', 12, 0]
      ]
    }
  },
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  features: {}
}

// Combo (cx/cn — sunburst/treemap; scx — chart/wordcloud). Nested answerVariants +
// answer keyed a{pos} with nested a{av} children (calcTreeData walks both trees in
// parallel). levels 2 + per-AV amounts drive the combo wordcloud branch (getComboWords
// → [avText, amount, position] triples).
export const DEFAULT_CX_DATA: any = {
  id: 'cx1',
  questionKey: 'q51',
  qType: 'cx',
  answerVariants: [
    {
      position: 1,
      text: 'Fruits',
      answerVariants: [
        { position: 1, text: 'Apple' },
        { position: 2, text: 'Banana' }
      ]
    },
    {
      position: 2,
      text: 'Vegetables',
      answerVariants: [
        { position: 1, text: 'Carrot' },
        { position: 2, text: 'Potato' }
      ]
    },
    { position: 3, text: 'Dairy' }
  ],
  answer: {
    total: 100,
    a1: { amount: 50, percents: 50, a1: { amount: 30, percents: 30 }, a2: { amount: 20, percents: 20 } },
    a2: { amount: 30, percents: 30, a1: { amount: 18, percents: 18 }, a2: { amount: 12, percents: 12 } },
    a3: { amount: 20, percents: 20 }
  },
  levels: 2,
  format: { useRawData: true },
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  features: {}
}

// Brand Perception (bpm) and the info-view quadrant (qa). bpm.json is a real staging `answer`: qv{n} per-brand
// per-attribute distributions, mds.{brands,attrs,center,version} MDS coordinates,
// mds.geoData.{brand,attr} elevation grids (TV surface), meanList/meanStat, scores
// {min,max,length}. questionVariants/batches are synthesized to match its 4×4 shape —
// texts are ours, positions must stay 1..4.
export const DEFAULT_BPM_DATA: any = {
  id: 'bpm1',
  questionKey: 'q60',
  qType: 'bpm',
  answer: bpmAnswer,
  questionVariants: [
    { position: 1, text: 'Brand Alpha' },
    { position: 2, text: 'Brand Beta' },
    { position: 3, text: 'Brand Gamma' },
    { position: 4, text: 'Brand Delta' }
  ],
  batches: [
    { position: 1, text: 'Quality' },
    { position: 2, text: 'Price' },
    { position: 3, text: 'Innovation' },
    { position: 4, text: 'Trust' }
  ],
  colorTheme: ['#4572A7', '#AA4643', '#89A54E', '#80699B'],
  features: {}
}

// Default data + params per chart-type, so testChartExport({chartType}) works bare.
export const FIXTURES: Record<string, { data: any; params?: Record<string, any> }> = {
  wc: { data: DEFAULT_WC_DATA, params: { width: 800, height: 600 } },
  // infographic:false → 1 PNG (DefaultView). Pass params:{infographic:true} for multi-output.
  vk: { data: DEFAULT_VK_DATA, params: { period: 0, infographic: false } },
  wd: { data: DEFAULT_VK_DATA, params: { period: 0, infographic: false } },
  // cjc/cjd → 1 PNG. No currentPackage → toProps median fallback. Pass
  // params:{currentPackage:'1.2.3'} to render a specific selection instead.
  cjc: { data: DEFAULT_CJ_DATA, params: { mode: 'exp' } },
  cjd: { data: DEFAULT_CJ_DATA, params: { mode: 'exp' } },
  // hm → 1 PNG (3000×1951). Default viewMode 'heatmap'; try params:{viewMode:'original'}
  // (bare image) or {clustersMode:'clusters-on'} (cluster overlay). Needs asset-service
  // access to fetch the background image.
  hm: { data: DEFAULT_HM_DATA, params: { viewMode: 'heatmap', clustersMode: 'clusters-off' } },
  // mx → 1 PNG (stacked matrix). Represents the whole Matrix family (sr/se/ps/mp/ro/aro
  // share MatrixChart). Try other viewModes/qTypes by passing data+params explicitly.
  mx: { data: DEFAULT_MX_DATA, params: { viewMode: 'stacked', chartMode: 'percents' } },
  // md → 1 PNG (MaxDiff probability columns).
  md: { data: DEFAULT_MD_DATA, params: { viewMode: 'probability', chartMode: 'respondents' } },
  // sft → 1 PNG (Rapid Association bar view).
  sft: { data: DEFAULT_SFT_DATA, params: { viewMode: 'bar', chartMode: 'respondents' } },
  // cf → 1 PNG (Correlation Finder grouped columns). groupType/axisType are required.
  cf: { data: DEFAULT_CF_DATA, params: { groupType: 'grouped', axisType: 'respondents', chartMode: 'columns' } },
  // psc → 1 PNG. mode = report-param psc{key}: 'd' (discrete) | 'pda'/'pdm' (delta
  // avg/median) | 'pba'/'pbm' (both-poles avg/median, pba = legacy default).
  psc: { data: DEFAULT_PSC_DATA, params: { mode: 'pba', neutral: 0 } },
  // histogram → 1 PNG (iv/Block → vk-family D3 chart). period = report-param
  // hg{key}.period, must be a key of answer.hist/curve (default bars[0]).
  histogram: { data: DEFAULT_HISTOGRAM_DATA, params: { period: '10' } },
  // sh → 1 PNG (3000×1951 shelf composite: photo + heatmap overlay). shelfViewMode =
  // report-param tscv{key}: 'heatmap' (default) | 'pheatmap' | 'clicks' | 'shelfs'
  // (bare image, no overlay). Image load polls at 1s — expect ~1-2s to chartReady.
  sh: { data: DEFAULT_SH_DATA, params: { shelfViewMode: 'heatmap' } },
  // emd → 1 PNG (734×555 composite). params = report-param emd{key} fields:
  // viewMode 'windmap' (default; line + windmap canvas + heatmap mask — waits ~2s
  // for particle trails) | 'timeslice' (capsule marker, instant) | 'aggregated'
  // (area fill + overlays). lineMode 'precise'|'smooth'|'segmented'. videoTime
  // snaps to the nearest pointDensity time (fixture spans 0..11s).
  emd: { data: DEFAULT_EMD_DATA, params: { viewMode: 'windmap', lineMode: 'precise', videoTime: 1 } },
  // oe family → 1 PNG for viewMode wordcloud/sunburst/treemap; verbatim/sentiments/
  // tags/chart → explicit error event (metadata-only in legacy PPTX). wcConfig =
  // report-param wc{id}; scope = combo drill-down (e.g. [1, '*']).
  oe: { data: DEFAULT_OE_DATA, params: { viewMode: 'wordcloud', width: 800, height: 600 } },
  // cx + viewMode 'wordcloud' exercises the combo-AV word path ([avText, amount,
  // position] triples; the dispatcher zeroes stop1..5 for cx — Combo.WcView parity).
  cx: { data: DEFAULT_CX_DATA, params: { viewMode: 'sunburst' } },
  cn: { data: DEFAULT_CX_DATA, params: { viewMode: 'sunburst' } },
  // scx default is 'chart' (metadata-only → error) — the fixture goes wordcloud via
  // the cloudWords fallback (scx has no nested answerVariants on live).
  scx: {
    data: { ...DEFAULT_OE_DATA, id: 'scx1', questionKey: 'q52', qType: 'scx' },
    params: { viewMode: 'wordcloud', width: 800, height: 600 }
  },
  // bpm → 1 PNG. mode 'm' MDS (default for bpm) | 't' Topography View (bundled d3 v3
  // engine, app/stats/components/topography) | 'q' Quadrant (HC bubble).
  // Value params (mds/tv/quadrant *Value) default to all-positions / legacy defaults.
  bpm: { data: DEFAULT_BPM_DATA, params: { mode: 'm' } },
  // qa is a question of its own, drawn by the info-view quadrant chart: it reads no sub-mode
  // and derives its own scores from the same answer. There is no `quadrant` chart type -- that
  // name is the editor-schema alias for qa and never reaches stats.
  qa: { data: { ...DEFAULT_BPM_DATA, qType: 'qa' }, params: {} }
}
